<template>
  <div id="searchDiv">
    <b-form action="#" @submit.prevent="submitSearch" @reset.prevent="onReset">
      <div class="container container--max-810">
        <div class="text-right mb-2">
          <b-link href="#" :aria-expanded="showAdvanced ? 'true' : 'false'" aria-controls="collapse-advanced-search-fields" @click="showAdvanced = !showAdvanced">
            {{ $t("lang.label_certificate_advanced_search") }}
          </b-link>
        </div>
        <b-form-group>
          <div class="position-relative">
            <b-form-input id="input-search-simple" v-model="form.simple" type="text" :placeholder="$t('lang.label_placeholder_searchCertificates')" class="has-icon" size="lg" />
            <div class="form-control-icon" @click="submitSearch">
              <mtg-icon icon="ic-search" viewBox="0 0 24 24"></mtg-icon>
            </div>
          </div>
        </b-form-group>
        <b-collapse id="collapse-advanced-search-fields" v-model="showAdvanced">
          <b-row>
            <b-form-group class="col-6 col-lg-4" :label="$t('lang.label_certificate_certStatus')" label-for="select-status">
              <multiselect
                id="select-status"
                :showLabels="false"
                v-model="form.certStatus"
                :multiple="true"
                :options="certificateStatusOptions"
                :placeholder="$t('lang.label_certificate_select')"
                :custom-label="normalizeCertStatusLabel"
                :show-labels="false"
              >
                <template slot="option" slot-scope="props">
                  {{ $t("lang.enum_certificateStatus_" + props.option) }}
                </template>
              </multiselect>
            </b-form-group>
            <b-form-group class="col-6 col-lg-4" :label="$t('lang.label_certificate_publicKeyAlgorithm_short')" label-for="select-public-key-algorithm">
              <multiselect
                id="select-public-key-algorithm"
                v-model="form.certPublicKeyAlgorithm"
                :multiple="true"
                :placeholder="$t('lang.label_certificate_select')"
                :options="publicKeyAlgorithmOptions"
                :custom-label="normalizePublicAlgorithmLabel"
                :show-labels="false"
              >
                <template slot="option" slot-scope="props">
                  {{ $t("lang.enum_publicKeyAlgorithm_" + props.option) }}
                </template>
              </multiselect>
            </b-form-group>
            <b-form-group class="col-6 col-lg-4" :label="$t('lang.label_certificate_signatureAlgorithm')" label-for="select-signature-algorithm">
              <multiselect
                id="select-signature-algorithm"
                v-model="form.certSignatureAlgorithm"
                :multiple="true"
                :placeholder="$t('lang.label_certificate_select')"
                :options="signatureAlgorithmOptions"
                :custom-label="normalizeSignatureAlgorithmLabel"
                :show-labels="false"
              >
                <template slot="option" slot-scope="props">
                  {{ $t("lang.enum_signatureAlgorithm_" + props.option) }}
                </template>
              </multiselect>
            </b-form-group>
            <!-- Valid From Date Range -->
            <b-form-group class="col-6" :label="$t('lang.label_certificate_validNotBefore_filter')" label-for="datepicker-valid-from">
              <div class="position-relative">
                <v-date-picker
                  id="datepicker-valid-from"
                  mode="range"
                  v-model="form.validFrom"
                  :input-props="{
                    class: 'form-control has-icon',
                    placeholder: $t('lang.label_placeholder_from')
                  }"
                  :masks="formats"
                />
                <div class="form-control-icon">
                  <mtg-icon icon="ic-calendar"></mtg-icon>
                </div>
              </div>
            </b-form-group>
            <!-- Valid To Date Range -->
            <b-form-group class="col-6" :label="$t('lang.label_certificate_validNotAfter_filter')" label-for="datepicker-valid-to">
              <div class="position-relative">
                <v-date-picker
                  id="datepicker-valid-to"
                  mode="range"
                  v-model="form.validTo"
                  :input-props="{
                    class: 'form-control has-icon',
                    placeholder: $t('lang.label_placeholder_to')
                  }"
                  :masks="formats"
                />
                <div class="form-control-icon">
                  <mtg-icon icon="ic-calendar"></mtg-icon>
                </div>
              </div>
            </b-form-group>

            <b-form-group class="col-6" :label="$t('lang.label_certificate_subjectDn')" label-for="input-subject-dn">
              <b-form-input id="input-subject-dn" type="text" :placeholder="$t('lang.label_placeholder_searchCertificates')" v-model="form.subjectDn" />
            </b-form-group>
            <b-form-group class="col-6" :label="$t('lang.label_certificate_issuerDn')" label-for="input-issuer-dn">
              <b-form-input id="input-issuer-dn" type="text" :placeholder="$t('lang.label_placeholder_searchCertificates')" v-model="form.issuerDn" />
            </b-form-group>
            <b-form-group class="col-6 col-lg-6" :label="$t('lang.label_certificate_serialNumber')" label-for="input-serial-number">
              <b-form-input id="input-serial-number" v-model="form.serialNumber" type="text" :placeholder="$t('lang.label_placeholder_enterText')" />
            </b-form-group>
            <b-form-group class="col-6 col-lg-6" :label="$t('lang.label_certificate_certUsageType')" label-for="select-cert-usage-type">
              <multiselect
                id="select-cert-usage-type"
                v-model="form.certUsageType"
                :multiple="true"
                :placeholder="$t('lang.label_certificate_select')"
                :options="certUsageTypeOptions"
                :custom-label="normalizeUsageTypeLabel"
                :show-labels="false"
              >
                <template slot="option" slot-scope="props">
                  {{ $t("lang.enum_keyUsageType_" + props.option) }}
                </template>
              </multiselect>
            </b-form-group>
          </b-row>
          <div class="d-flex justify-content-end mt-2">
            <button class="btn btn-outline-primary" type="reset">
              {{ $t("lang.label_certificate_reset") }}
            </button>
            <button class="btn btn-primary ml-3" type="submit">
              {{ $t("lang.label_certificate_apply") }}
            </button>
          </div>
        </b-collapse>
      </div>
    </b-form>
  </div>
</template>
<script>
import CertificateStatus from "@/api/enums/CertificateStatus";
import PublicKeyAlgorithm from "@/api/enums/PublicKeyAlgorithm";
import SignatureAlgorithm from "../../api/enums/SignatureAlgorithm";
import KeyUsageType from "../../api/enums/KeyUsageType";
import * as utils from "../../utils";
import Multiselect from "vue-multiselect";
export default {
  name: "SearchCertificatesForm",
  props: {
    // Support v-model via value/emit on this.search
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showAdvanced: false,
      form: { ...this.value }, // Make a local copy
      certificateStatusOptions: [CertificateStatus.ACTIVE, CertificateStatus.REVOKED, CertificateStatus.EXPIRED],
      publicKeyAlgorithmOptions: [PublicKeyAlgorithm.RSA, PublicKeyAlgorithm.EC, PublicKeyAlgorithm.CME, PublicKeyAlgorithm.SPX],
      signatureAlgorithmOptions: [SignatureAlgorithm.SHA256_WITH_SPX, SignatureAlgorithm.SHA256_WITH_RSA, SignatureAlgorithm.SHA256_WITH_ECDSA],
      certUsageTypeOptions: [KeyUsageType.TLS_SERVER, KeyUsageType.TLS_CLIENT, KeyUsageType.SMIME],
      formats: {
        input: [utils.constants.DATE_PATTERN]
      },
      value1: [],
      options: ["list", "of", "options"]
    };
  },
  methods: {
    submitSearch() {
      // Forward searches to the parent component.
      this.$emit("input", this.form);
    },
    onReset() {
      this.$store.commit("certificate/reset_searchFilter");
    },
    normalizeUsageTypeLabel(option) {
      return this.$i18n.t("lang.enum_keyUsageType_" + option);
    },
    normalizeSignatureAlgorithmLabel(option) {
      return this.$i18n.t("lang.enum_signatureAlgorithm_" + option);
    },
    normalizePublicAlgorithmLabel(option) {
      return this.$i18n.t("lang.enum_publicKeyAlgorithm_" + option);
    },
    normalizeCertStatusLabel(option) {
      return this.$i18n.t("lang.enum_certificateStatus_" + option);
    }
  },
  watch: {
    value: function(newValue) {
      this.form = { ...newValue }; // Update the local copy
    }
  },
  mounted() {
    if (!this.$route.params.isFromChart) this.$store.commit("certificate/reset_searchFilter");
  },
  components: {
    Multiselect
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.multiselect {
  border: 2px solid #a5c5e0;
}

.multiselect__tag {
  background: lightgrey;
  color: #7d7d7d;
}

.multiselect__tag-icon::after {
  color: #7d7d7d;
}
.multiselect__option--selected.multiselect__option--highlight {
  background: lightgrey;
}
.multiselect__option--highlight {
  background: lightgrey;
  outline: none;
  color: #fff;
}
.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background: lightgrey;
}
</style>
